import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject, Observable, timer, of } from 'rxjs';
import { filter, catchError, take, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { environment } from '../../environments/environment';
import { JwtSignRequest, JwtSignResponse } from '../models/jwt-sign.model';
import { AuthService } from '../auth.service';
import * as ReviewSelectors from '../views/ccm/state/review-page.selectors';
import { ToastNotificationService } from './toast-notification.service';

@Injectable({
  providedIn: 'root'
})
export class HucuService implements OnDestroy {
  private readonly REVIEW_PAGE_ROUTES = [
    '/ccm/patients/review-page',
    '/bhi/patients/review-page',
    '/rpm/reviews/patients-details'
  ];

  private readonly DEFAULT_IFRAME_LOADING_DELAY_MS = 2000;
  private readonly DEFAULT_NOTIFICATION_FREQUENCY_MINUTES = 1;

  private readonly _iframeUrl$ = new BehaviorSubject<SafeResourceUrl | null>(null);
  private readonly _loading$ = new BehaviorSubject<boolean>(false);
  private readonly _notificationIframeUrl$ = new BehaviorSubject<SafeResourceUrl | null>(null);
  private readonly _unreadCount$ = new BehaviorSubject<number>(0);
  private readonly _backendBaseUrl: string = environment.backEndUrl;
  private previousRoute: string = '';
  private _patientId: string | null = null;
  private _locationId: string | null = null;
  private _userEmail: string = '';

  // Public observables
  readonly iframeUrl$: Observable<SafeResourceUrl | null> = this._iframeUrl$.asObservable();
  readonly loading$: Observable<boolean> = this._loading$.asObservable();
  readonly notificationIframeUrl$: Observable<SafeResourceUrl | null> = this._notificationIframeUrl$.asObservable();
  readonly unreadCount$: Observable<number> = this._unreadCount$.asObservable();

  private readonly _onMessage = (event: MessageEvent): void => {
    if (event.origin !== new URL(environment.hucuIntegrationUrl).origin) return;
    if (event.data?.type !== 'HUCU_NOTIFICATION_SYNC') return;
    const { unreadCount } = event.data.payload;
    if (!Number.isNaN(unreadCount) && unreadCount >= 0) {
      this._unreadCount$.next(unreadCount);
    }
  };

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly toastNotificationService: ToastNotificationService,
    private readonly store: Store,
    private readonly authService: AuthService
  ) {
    // Cache user email during construction (updates when store profile loads)
    this.initializeUserEmail();

    // Initialize previous route with current route
    this.previousRoute = this.router.url;
    this.setupRouteListener();
    window.addEventListener('message', this._onMessage);
  }

  /** Remove window message listener to prevent memory leaks */
  ngOnDestroy(): void {
    window.removeEventListener('message', this._onMessage);
  }

  /**
   * Initialize and cache user email from profile or OAuth claims
   * Called once during construction
   */
  private initializeUserEmail(): void {
    // Start with OAuth claims so we have an immediate fallback value
    const claims: any = this.authService.claims;
    this._userEmail = claims?.email || '';

    // Wait for profile email (if/when store is populated) and override cached value
    this.store.select(ReviewSelectors.selectProfile)
      .pipe(
        map(profile => profile?.who?.e_mail?.trim() || null),
        filter((email): email is string => !!email),
        take(1)
      )
      .subscribe(email => {
        this._userEmail = email;
      });
  }

  /**
   * Set the patient ID for review page context.
   * Called by review page components when they load.
   * Invalidates the current iframe URL so it will be rebuilt with new patient context.
   * @param patientId The patient ID to include in JWT payload
   */
  setPatientId(patientId: string | null): void {
    this._patientId = patientId;
    this._iframeUrl$.next(null); // Invalidate - URL will rebuild when sidebar opens
  }

  /**
   * Get current patient ID
   */
  get patientId(): string | null {
    return this._patientId;
  }

  /**
   * Set the location ID for facility context.
   * Automatically called when facility filter changes.
   * Invalidates the current iframe URL so it will be rebuilt with new location context.
   * @param locationId The facility global ID to include in JWT payload
   */
  setLocationId(locationId: string | null): void {
    if (locationId === this._locationId) return;

    this._locationId = locationId;
    this._iframeUrl$.next(null); // Invalidate - URL will rebuild when sidebar opens
  }

  /**
   * Get current location ID
   */
  get locationId(): string | null {
    return this._locationId;
  }

  /**
   * Initialize iframe URL - called when sidebar opens for the first time
   */
  initializeIframe(): void {
    // Only initialize if URL doesn't exist
    if (!this._iframeUrl$.value) {
      this.loadIframeUrl();
    }
  }

  /**
   * Check if a route is a review page
   */
  private isReviewPage(route: string): boolean {
    return this.REVIEW_PAGE_ROUTES.some(reviewRoute => route.includes(reviewRoute));
  }

  /**
   * Setup router listener to detect route changes
   * Clears patient ID when navigating to or from review pages
   */
  private setupRouteListener(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe(event => {
        const currentRoute = event.urlAfterRedirects;
        const isCurrentReviewPage = this.isReviewPage(currentRoute);
        const isPreviousReviewPage = this.isReviewPage(this.previousRoute);

        // Clear patient ID when navigating TO or FROM review pages
        // This triggers URL invalidation via setPatientId()
        if (this.previousRoute && (isCurrentReviewPage || isPreviousReviewPage)) {
            this.setPatientId(null);
        }

        this.previousRoute = currentRoute;
      });
  }

  /**
   * Build base JWT payload with required fields
   */
  private buildPayload(): JwtSignRequest {
    const payload: JwtSignRequest = {
      iat: Date.now(), // UTC timestamp in milliseconds
      user: {
        internalId: this._userEmail,
      },
    };

    // Add location data if available
    if (this._locationId) {
      payload['location'] = {
        internalId: this._locationId,
      };
    }
    // Add patient ID if available
    if (this._patientId) {
      payload['patient'] = {
        internalId: this._patientId,
      };
    }

    return payload;
  }

  /**
   * Validate Hucu service configuration
   * @returns true if configuration is valid, false otherwise
   */
  private validateConfiguration(): boolean {
    if (!environment.hucuIntegrationUrl || !environment.hucuPublicKey) {
      this.toastNotificationService.showError('Hucu integration URL or public key is not configured. Please contact support.');
      return false;
    }

    if (!this._userEmail || this._userEmail.trim() === '') {
      this.toastNotificationService.showError('User email is not available. Please ensure you are logged in.');
      return false;
    }

    return true;
  }

  /**
   * Load iframe URL by fetching JWT token and building URL
   */
  private loadIframeUrl(): void {
    // Validate configuration
    if (!this.validateConfiguration()) {
      return;
    }

    // Show loading state
    this._loading$.next(true);
    this._iframeUrl$.next(null);

    // Build JWT payload with cached email and optional patient ID
    const payload = this.buildPayload();

    // Fetch JWT token
    this.signJwt(payload)
      .pipe(
        catchError(error => {
          this._loading$.next(false);
          this.toastNotificationService.showError('Failed to load Hucu integration. Please try again later.');
          return of(null);
        })
      )
      .subscribe(response => {
        if (response?.signedJwt) {
          // Construct the integration URL and sanitize it for iframe
          const url = this.buildIframeUrl(response.signedJwt);
          this._iframeUrl$.next(url);

          // Keep loading state active for additional delay to allow iframe initialization
          const delayMs = environment.hucuIframeLoadingDelayMs || this.DEFAULT_IFRAME_LOADING_DELAY_MS;
          timer(delayMs).subscribe(() => {
            this._loading$.next(false);
          });
        } else {
          // If no response, hide loading immediately
          this._loading$.next(false);
        }
      });
  }

  /**
   * Sign JWT token with the provided payload
   * @param payload JSON payload to be signed
   * @returns Observable containing the signed JWT token and public key
   */
  private signJwt(payload: JwtSignRequest): Observable<JwtSignResponse> {
    return this.http.post<JwtSignResponse>(`${this._backendBaseUrl}/hucu/jwt/sign`, payload);
  }

  /**
   * Build iframe URL from token
   */
  private buildIframeUrl(token: string): SafeResourceUrl {
    const url = `${environment.hucuIntegrationUrl}/login/integrations/validate/${environment.hucuPublicKey}?payload=${encodeURIComponent(token)}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url); // NOSONAR - URL is constructed from controlled environment config, not user input
  }

  /**
   * Build notification data-transporter iframe URL from signed JWT
   */
  private buildNotificationIframeUrl(signedJwt: string): SafeResourceUrl {
    const frequency =
      environment.hucuNotificationFrequencyMinutes ||
      this.DEFAULT_NOTIFICATION_FREQUENCY_MINUTES;
    const url =
      `${environment.hucuIntegrationUrl}/data-transporter` +
      `?token=${encodeURIComponent(signedJwt)}` +
      `&publicKey=${encodeURIComponent(environment.hucuPublicKey)}` +
      `&frequency=${frequency}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url); // NOSONAR - URL is constructed from controlled environment config, not user input
  }

  /**
   * Load notification iframe URL by fetching JWT token and building URL
   */
  private loadNotificationIframeUrl(): void {
    if (!this.validateConfiguration()) return;

    const payload = this.buildPayload();

    this.signJwt(payload)
      .pipe(
        catchError(_error => {
          console.warn('[HucuService] Failed to load notification iframe URL', _error);
          return of(null);
        })
      )
      .subscribe(response => {
        if (response?.signedJwt) {
          this._notificationIframeUrl$.next(
            this.buildNotificationIframeUrl(response.signedJwt)
          );
        }
      });
  }

  /** Initialize notification iframe URL - called once from DefaultLayoutComponent on authenticated session start */
  initializeNotificationIframe(): void {
    if (!this._notificationIframeUrl$.value) {
      this.loadNotificationIframeUrl();
    }
  }
}

